// TutorIaHero — bloco "estrela" da home do aluno pra Tira-dúvida (Tutor IA).
// Substitui o QuickStudyCard quando o aluno o ativa na home. Diferenças:
//   - Hero visualmente maior, gradiente, ocupa toda a largura quando xl
//   - Mostra textarea pronto pra digitar sem clicar primeiro
//   - Lista 3 dúvidas recentes em chips clicáveis (continuar de onde parou)
//   - Botões câmera/galeria/PDF visíveis no mobile
//
// Conecta no mesmo store do QuickStudy — não duplica estado.

import { useEffect } from "preact/hooks";
import { ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import {
  loadQuickStudyConversations,
  loadQuickStudyLimits,
  openQuickStudyConversation,
  quickStudyConversations,
  quickStudyLimits
} from "./quickStudyStore";
import { QuickStudyComposer } from "./QuickStudy";

interface Props {
  /** Disparado quando aluno clica em "Abrir histórico" ou conversa recente. */
  onOpenFull?: () => void;
}

export function TutorIaHero({ onOpenFull }: Props) {
  useEffect(() => {
    void loadQuickStudyLimits();
    void loadQuickStudyConversations();
  }, []);

  const limits = quickStudyLimits.value;
  if (!limits.enabled) return null;

  const recent = quickStudyConversations.value
    .filter((c) => !c.archived)
    .slice(0, 3);

  function popOut() {
    window.open(
      "?view=quickstudy-window",
      "quickstudy-new",
      "width=560,height=820,resizable=yes,scrollbars=yes"
    );
  }

  function openConversation(id: string) {
    void openQuickStudyConversation(id);
    onOpenFull?.();
  }

  return (
    <section
      class="relative overflow-hidden rounded-xl border border-violet-500/40 p-5 shadow-sm sm:p-6"
      style={{
        background:
          "linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(217,70,239,0.08) 100%)"
      }}
    >
      {/* Glow decorativo */}
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -right-12 -top-12 size-48 rounded-full opacity-40 blur-3xl sm:size-64"
        style={{ background: "radial-gradient(circle, #8b5cf6 0%, transparent 70%)" }}
      />

      <header class="relative mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-3">
          <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg sm:size-12">
            <Sparkles className="size-5 sm:size-6" />
          </div>
          <div>
            <h2 class="flex items-center gap-2 text-base font-bold sm:text-lg">
              Tutor IA
              <span class="rounded-full bg-violet-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                Tira-dúvida
              </span>
            </h2>
            <p class="text-xs text-muted-foreground sm:text-sm">
              Tire foto, anexe PDF ou digite. Resposta na hora — com matemática,
              fórmulas e passo a passo.
            </p>
          </div>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={popOut}
            title="Abrir em janela separada"
            aria-label="Abrir em janela"
            class="inline-flex items-center gap-1 rounded-md border border-violet-500/40 bg-card/60 px-2 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
          >
            <ExternalLink className="size-3" />
            Janela
          </button>
          {onOpenFull && (
            <button
              type="button"
              onClick={onOpenFull}
              class="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:underline dark:text-violet-300"
            >
              Histórico
              <ArrowRight className="size-3" />
            </button>
          )}
        </div>
      </header>

      <div class="relative">
        <QuickStudyComposer
          conversationId={null}
          autoCreate
          onSent={onOpenFull}
        />
      </div>

      {recent.length > 0 && (
        <div class="relative mt-4">
          <div class="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Continuar:
          </div>
          <div class="flex flex-wrap gap-1.5">
            {recent.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => openConversation(c.id)}
                class="inline-flex max-w-[260px] items-center gap-1.5 truncate rounded-full border border-violet-500/30 bg-card/60 px-3 py-1 text-[11px] font-medium hover:border-violet-500 hover:bg-violet-500/10"
                title={c.title}
              >
                {c.personaLabel && (
                  <span class="text-violet-500">●</span>
                )}
                <span class="truncate">{c.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
